import { useQuery } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

/**
 * One row per (party × role × capacity). A single human party often
 * appears in multiple capacities (e.g., individually + as Co-Executor +
 * as Co-Trustee) so consumers usually group by party_id for display.
 */
export interface CasePartyRow {
  party_id: string;
  display_name: string;
  party_type: "individual" | "entity";
  role: string;
  capacity: string;
}

/**
 * Fetch all parties bound to a case via case_parties, joined with
 * client_parties for display info. Returns one row per capacity —
 * group client-side by party_id for the typical "one card per party,
 * with their capacities listed" view.
 */
export function useCaseParties(caseId: string | undefined) {
  return useQuery<CasePartyRow[], Error>({
    queryKey: ["case-parties", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    queryFn: async () => {
      if (!caseId) return [];

      // 1. Get case_parties rows for this case.
      const { data: caseParties, error: cpErr } = await berserkr()
        .from("case_parties")
        .select("party_id, role, capacity")
        .eq("case_id", caseId);
      if (cpErr) throw new Error(cpErr.message);
      if (!caseParties || caseParties.length === 0) return [];

      // 2. Look up the matching client_parties for display info.
      const partyIds = Array.from(
        new Set(caseParties.map((r: { party_id: string }) => r.party_id)),
      );
      const { data: clients, error: clErr } = await berserkr()
        .from("client_parties")
        .select("id, display_name, party_type")
        .in("id", partyIds);
      if (clErr) throw new Error(clErr.message);

      const byId = new Map<
        string,
        { display_name: string; party_type: "individual" | "entity" }
      >();
      for (const c of clients ?? []) {
        byId.set((c as { id: string }).id, {
          display_name: (c as { display_name: string }).display_name,
          party_type: ((c as { party_type: string }).party_type ===
          "entity"
            ? "entity"
            : "individual"),
        });
      }

      return caseParties.map(
        (cp: { party_id: string; role: string; capacity: string }) => ({
          party_id: cp.party_id,
          display_name:
            byId.get(cp.party_id)?.display_name ?? "Unknown party",
          party_type: byId.get(cp.party_id)?.party_type ?? "individual",
          role: cp.role,
          capacity: cp.capacity,
        }),
      );
    },
  });
}

/**
 * Helper: group a flat CasePartyRow[] by party_id, collapsing capacities
 * into an array under each party.
 */
export interface GroupedParty {
  party_id: string;
  display_name: string;
  party_type: "individual" | "entity";
  role: string;
  capacities: string[];
}

export function groupByParty(rows: CasePartyRow[]): GroupedParty[] {
  const map = new Map<string, GroupedParty>();
  for (const r of rows) {
    if (!map.has(r.party_id)) {
      map.set(r.party_id, {
        party_id: r.party_id,
        display_name: r.display_name,
        party_type: r.party_type,
        role: r.role,
        capacities: [],
      });
    }
    map.get(r.party_id)!.capacities.push(r.capacity);
  }
  return Array.from(map.values());
}
